import React, { useEffect, useState } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

/**
 * Returns the parsed timestamp (ms) for an ISO deadline, or null if invalid/missing.
 */
const parseDeadline = (deadline) => {
  if (!deadline) return null;
  const ts = Date.parse(deadline);
  if (Number.isNaN(ts)) return null;
  return ts;
};

/**
 * True if `deadline` is in the past relative to `now`.
 */
export const isDeadlineExpired = (deadline, now = Date.now()) => {
  const ts = parseDeadline(deadline);
  if (ts == null) return false;
  return now > ts;
};

/**
 * Coarse health state used for badge coloring.
 * - 'expired'  : now > deadline
 * - 'urgent'   : 0 <= remaining <= 4h
 * - 'warning'  : 4h < remaining <= 24h
 * - 'healthy'  : remaining > 24h
 */
export const getDeadlineState = (deadline, now = Date.now()) => {
  const ts = parseDeadline(deadline);
  if (ts == null) return 'healthy';
  const diffMs = ts - now;
  if (diffMs <= 0) return 'expired';
  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours <= 4) return 'urgent';
  if (diffHours <= 24) return 'warning';
  return 'healthy';
};

/**
 * Formats remaining time relative to `now`.
 * compact:  "1d 4h", "3h 12m", "9m", "<1m", "expired 2h ago"
 * !compact: "1 day 4 hours", "3 hours 12 minutes", "less than a minute"
 */
export const formatRemaining = (deadline, compact = true, now = Date.now()) => {
  const ts = parseDeadline(deadline);
  if (ts == null) return '';
  const diffMs = ts - now;
  const isPast = diffMs <= 0;
  const absMs = Math.abs(diffMs);

  const totalMinutes = Math.floor(absMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (compact) {
    let body;
    if (days > 0) {
      body = `${days}d ${hours}h`;
    } else if (hours > 0) {
      body = `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      body = `${minutes}m`;
    } else {
      body = '<1m';
    }
    return isPast ? `expired ${body} ago` : body;
  }

  const parts = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (minutes > 0 && days === 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  const body = parts.length > 0 ? parts.join(' ') : 'less than a minute';
  return isPast ? `expired ${body} ago` : body;
};

const STATE_CLASSES = {
  healthy: 'bg-slate-100 text-slate-700 border border-slate-200',
  warning: 'bg-amber-50 text-amber-700 border border-amber-200',
  urgent: 'bg-rose-50 text-rose-700 border border-rose-200',
  expired: 'bg-rose-100 text-rose-700 border border-rose-300',
};

const SIZE_CLASSES = {
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-1',
};

const ICON_SIZE = {
  sm: 12,
  md: 14,
};

/**
 * Pill-shaped countdown badge that re-renders every minute.
 * Returns null when `deadline` is missing or unparseable.
 */
const CountdownBadge = ({
  deadline,
  label,
  expiredLabel,
  size = 'sm',
  compact = true,
  className = '',
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!deadline) return undefined;
    // Re-render every 60s so the displayed remaining time stays current.
    const id = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, [deadline]);

  const ts = parseDeadline(deadline);
  if (ts == null) return null;

  const state = getDeadlineState(deadline, now);
  const expired = state === 'expired';
  const remainingText = formatRemaining(deadline, compact, now);
  const Icon = expired || state === 'urgent' ? AlertTriangle : Clock;

  const stateClass = STATE_CLASSES[state] || STATE_CLASSES.healthy;
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  const iconSize = ICON_SIZE[size] || ICON_SIZE.sm;

  const visibleLabel = expired ? (expiredLabel || label) : label;
  const fullTitle = `${visibleLabel ? `${visibleLabel} — ` : ''}${remainingText}`;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeClass} ${stateClass} ${className}`}
      title={fullTitle}
    >
      <Icon size={iconSize} className="shrink-0" aria-hidden />
      {visibleLabel ? (
        <span className="whitespace-nowrap">
          {visibleLabel}
          <span className="font-normal opacity-80"> · </span>
          <span className="font-semibold tabular-nums">{remainingText}</span>
        </span>
      ) : (
        <span className="font-semibold tabular-nums whitespace-nowrap">{remainingText}</span>
      )}
    </span>
  );
};

export default CountdownBadge;
